import { ColumnConfig } from '@components/design/migrated/Table';
import { ColumnHeader, ColumnHeaderDisplayName, SaleType } from '@models/Products';
import * as styles from '../ProductsTable.module.scss';
import { isMarketRank, isProductFeatureViewColumn, isRetailerRank } from '@utils/hook/tableCellConst/TableCellConst';
import { DisplayOptions } from '../products-table-filter/Types';
import { absoluteSortFields, sortFields, sortFieldsDisabled } from '@utils/TableUtils';
import { getIsDisabledStatus } from '@utils/productGroupUtils/ProductGroupUtils';

export const getProductsColumns = (
  selectedDisplayType: DisplayOptions,
  selectedSaleType: SaleType,
  features: (string | null)[],
  productSpecificationFilters,
  isAbsoluteSortOnly: boolean = false,
): ColumnConfig[] => {
  const commonColumnFields = {
    className: styles.ProductsTable__TableHeader,
    isSortable: true,
    sortFields: getColumnSortFields(productSpecificationFilters, isAbsoluteSortOnly),
    compact: true,
  };

  const featureViewColumnFields = {
    className: styles.ProductsTable__TableHeader,
    isSortable: false,
    compact: false,
  };
  const overviewViewColumnFields = {
    className: styles.ProductsTable__TableHeader,
    isSortable: false,
    compact: true,
  };

  const isDataView = selectedDisplayType === DisplayOptions.data;
  const isFeatureView = selectedDisplayType === DisplayOptions.feature;
  const isOverviewView = selectedDisplayType === DisplayOptions.overview;
  const isDataUnitsView = isDataView && selectedSaleType === SaleType.volume;
  const isDataRevenueView = isDataView && selectedSaleType === SaleType.revenue;

  const columns = [
    {
      headerKey: ColumnHeader.marketRank.toString(),
      name: ColumnHeaderDisplayName[ColumnHeader.marketRank],
      isHidden: !isDataView && !isOverviewView,
    },
    {
      headerKey: ColumnHeader.marketRevenue.toString(),
      name: ColumnHeaderDisplayName[ColumnHeader.marketRevenue],
      isHidden: !isDataRevenueView && !isOverviewView,
    },
    {
      headerKey: ColumnHeader.marketExcludeRetailRevenue.toString(),
      name: ColumnHeaderDisplayName[ColumnHeader.marketExcludeRetailRevenue],
      isHidden: !isDataRevenueView && !isOverviewView,
    },
    {
      headerKey: ColumnHeader.marketVolume.toString(),
      name: ColumnHeaderDisplayName[ColumnHeader.marketVolume],
      isHidden: !isDataUnitsView && !isOverviewView,
    },
    {
      headerKey: ColumnHeader.marketExcludeRetailVolume.toString(),
      name: ColumnHeaderDisplayName[ColumnHeader.marketExcludeRetailVolume],
      isHidden: !isDataUnitsView && !isOverviewView,
    },
    {
      headerKey: ColumnHeader.marketShareRevenue.toString(),
      name: ColumnHeaderDisplayName[ColumnHeader.marketShareRevenue],
      isHidden: !isDataRevenueView,
    },
    {
      headerKey: ColumnHeader.marketShareVolume.toString(),
      name: ColumnHeaderDisplayName[ColumnHeader.marketShareVolume],
      isHidden: !isDataUnitsView,
    },
    {
      headerKey: ColumnHeader.marketPrice.toString(),
      name: ColumnHeaderDisplayName[ColumnHeader.marketPrice],
      isHidden: !isDataView,
    },
    {
      headerKey: ColumnHeader.marketExcludeRetailPrice.toString(),
      name: ColumnHeaderDisplayName[ColumnHeader.marketExcludeRetailPrice],
      isHidden: !isDataView,
    },
    {
      headerKey: ColumnHeader.retailerRank.toString(),
      name: ColumnHeaderDisplayName[ColumnHeader.retailerRank],
      isHidden: !isDataView,
    },
    {
      headerKey: ColumnHeader.retailerRevenue.toString(),
      name: ColumnHeaderDisplayName[ColumnHeader.retailerRevenue],
      isHidden: !isDataRevenueView && !isOverviewView,
    },
    {
      headerKey: ColumnHeader.retailerVolume.toString(),
      name: ColumnHeaderDisplayName[ColumnHeader.retailerVolume],
      isHidden: !isDataUnitsView && !isOverviewView,
    },
    {
      headerKey: ColumnHeader.retailerShareRevenue.toString(),
      name: ColumnHeaderDisplayName[ColumnHeader.retailerShareRevenue],
      isHidden: !isDataRevenueView,
    },
    {
      headerKey: ColumnHeader.retailerShareVolume.toString(),
      name: ColumnHeaderDisplayName[ColumnHeader.retailerShareVolume],
      isHidden: !isDataUnitsView,
    },
    {
      headerKey: ColumnHeader.retailerMixRevenue.toString(),
      name: ColumnHeaderDisplayName[ColumnHeader.retailerMixRevenue],
      isHidden: !isDataRevenueView,
    },
    {
      headerKey: ColumnHeader.retailerMixVolume.toString(),
      name: ColumnHeaderDisplayName[ColumnHeader.retailerMixVolume],
      isHidden: !isDataUnitsView,
    },
    {
      headerKey: ColumnHeader.retailerPrice.toString(),
      name: ColumnHeaderDisplayName[ColumnHeader.retailerPrice],
      isHidden: !isDataView,
    },
    {
      headerKey: ColumnHeader.firstActivity.toString(),
      name: ColumnHeaderDisplayName[ColumnHeader.firstActivity],
      isHidden: !isFeatureView && !isOverviewView,
    },
  ];

  const featureColumns = features.map((f, i) => {
    return {
      headerKey: `${ColumnHeader.feature}_${i}_${f}`,
      name: f!.toString(),
      isHidden: !isFeatureView,
    };
  });

  return columns
    .concat(featureColumns)
    .map((column) => {
      if (
        isProductFeatureViewColumn(column.headerKey) ||
        isRetailerRank(column.headerKey) ||
        isMarketRank(column.headerKey)
      ) {
        return { ...featureViewColumnFields, ...column };
      }

      if (isOverviewView) {
        return { ...overviewViewColumnFields, ...column };
      }

      return { ...commonColumnFields, ...column };
    })
    .filter((column) => !column.isHidden);
};

export const getColumnSortFields = (productSpecificationFilters: any, isAbsoluteSortOnly: boolean) => {
  if (isAbsoluteSortOnly) {
    return absoluteSortFields;
  }

  if (getIsDisabledStatus(productSpecificationFilters)) {
    return sortFieldsDisabled;
  }

  return sortFields;
};
